import { useState, useEffect } from 'react';
import { Key, Globe, Server, Cpu, ShieldCheck, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import api from '../services/api';

const normalizeDomain = (value = '') => {
  let domain = String(value).trim().toLowerCase();
  domain = domain.replace(/^https?:\/\//, '');

  const [hostPart, ...pathParts] = domain.split('/');
  const host = hostPart.replace(/:\d+$/, '');
  const path = pathParts.join('/').replace(/^\/+|\/+$/g, '');

  return path ? `${host}/${path}` : host;
};

const appBasePath = process.env.PUBLIC_URL || '';
const appIdentityDefault = normalizeDomain(
  `${window.location.hostname}${appBasePath ? `/${String(appBasePath).replace(/^\/+|\/+$/g, '')}` : ''}`
);
const goToAppHome = () => {
  window.location.href = `${appBasePath}/`;
};

export default function LicenseSetup() {
  const [licenseKey, setLicenseKey] = useState('');
  const [domain, setDomain] = useState(appIdentityDefault);
  const [serverHash, setServerHash] = useState('');
  const [licenseServerUrl, setLicenseServerUrl] = useState('');
  const [renewUrl, setRenewUrl] = useState('');
  const [status, setStatus] = useState('pending'); // pending, active, invalid
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchInfo();
  }, []);

  // Automatic polling for renewal detection
  useEffect(() => {
    let interval;
    if (status === 'invalid' && !success && !loading) {
      interval = setInterval(() => {
        console.log('[License] Auto-checking for renewal...');
        handleCheckRenewal(true); // pass true to indicate silent check
      }, 10000); // Check every 10 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [status, success, loading]);

  const fetchInfo = async () => {
    try {
      const res = await api.get('/license/info');
      setServerHash(res.data.serverHash);
      setStatus(res.data.status);
      setLicenseServerUrl(res.data.licenseServerUrl);
      setRenewUrl(res.data.renewUrl || '');
      if (res.data.licenseKey) {
        setLicenseKey(res.data.licenseKey);
      }
    } catch (err) {
      console.error('Error fetching info:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const normalizedDomain = normalizeDomain(domain);
      const res = await api.post('/license/activate', {
        licenseKey: licenseKey.trim(),
        domain: normalizedDomain
      });
      if (res.data.success) {
        setSuccess(true);
        setTimeout(() => {
          goToAppHome();
        }, 2000);
      }
    } catch (err) {
      const apiError = err.response?.data;
      const baseMessage = apiError?.error || 'Activation failed. Please check your key and connection.';
      const attemptedDomain = apiError?.attemptedDomain;
      const upstream = apiError?.upstream;
      const upstreamSummary = upstream
        ? [
            upstream.type ? `type=${upstream.type}` : null,
            upstream.status ? `status=${upstream.status}` : null,
            upstream.code ? `code=${upstream.code}` : null,
            upstream.message ? `message=${upstream.message}` : null
          ]
            .filter(Boolean)
            .join(', ')
        : '';
      setError(
        [
          baseMessage,
          attemptedDomain ? `(attempted domain: ${attemptedDomain})` : '',
          upstreamSummary ? `(upstream: ${upstreamSummary})` : ''
        ]
          .filter(Boolean)
          .join(' ')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCheckRenewal = async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    if (!isSilent) setError('');
    try {
      const res = await api.post('/license/check');
      if (res.data.status === 'active') {
        setSuccess(true);
        setTimeout(() => {
          goToAppHome();
        }, 2000);
      } else if (!isSilent) {
        setError(res.data.error || 'License is still showing as expired on the server.');
      }
    } catch (err) {
      if (!isSilent) setError('Could not connect to the server. Please try again.');
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  const isRenewal = status === 'invalid';

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-main)' }}>
        <Loader2 className="animate-spin" style={{ color: 'var(--brand)' }} size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden" style={{ backgroundColor: 'var(--bg-main)' }}>
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: isRenewal ? 'var(--danger, #f43f5e)' : 'var(--brand)', opacity: 0.06 }}></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: isRenewal ? 'var(--danger, #f43f5e)' : 'var(--brand)', opacity: 0.06 }}></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center">
          <div className="p-4 rounded-2xl text-white shadow-2xl transition-all duration-500" style={{ backgroundColor: isRenewal ? '#f43f5e' : 'var(--brand)', boxShadow: `0 0 60px ${isRenewal ? '#f43f5e' : 'var(--brand)'}` }}>
            {isRenewal ? <AlertCircle size={40} /> : <ShieldCheck size={40} />}
          </div>
        </div>
        <h2 className="mt-8 text-center text-3xl font-black tracking-tight uppercase" style={{ color: 'var(--text-main)' }}>
          {isRenewal ? 'Renewal Required' : 'Installation Wizard'}
        </h2>
        <p className="mt-2 text-center text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
          {isRenewal ? 'Your license has expired or is invalid' : 'Activate your POS license to continue'}
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="py-10 px-6 shadow-2xl sm:rounded-2xl sm:px-10" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          {success ? (
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center p-4 bg-emerald-500/10 text-emerald-500 rounded-full animate-bounce">
                <ShieldCheck size={48} />
              </div>
              <h3 className="text-xl font-black" style={{ color: 'var(--text-main)' }}>System Activated!</h3>
              <p className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>Redirecting you to the login screen...</p>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleActivate}>
              {error && (
                <div className="bg-rose-500/10 text-rose-500 p-4 rounded-xl text-xs font-black border border-rose-500/20 flex items-center gap-3 uppercase tracking-widest leading-relaxed">
                  <AlertCircle size={18} className="shrink-0" />
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>License Key</label>
                <div className="relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Key className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />
                  </div>
                  <input
                    required
                    className="block w-full pl-12 sm:text-sm rounded-xl py-3 outline-none transition-all focus:ring-2 uppercase"
                    style={{ backgroundColor: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)', '--tw-ring-color': 'var(--brand)' }}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    value={licenseKey}
                    onChange={e => setLicenseKey(e.target.value)}
                  />
                </div>
              </div>

              {isRenewal && (renewUrl || licenseServerUrl) && (
                <div className="flex flex-col gap-3">
                  <div className="p-4 rounded-xl flex items-center justify-between gap-4" style={{ backgroundColor: 'rgba(244, 63, 94, 0.05)', border: '1px dashed #f43f5e' }}>
                    <div className="text-[10px] font-bold uppercase tracking-widest leading-tight" style={{ color: '#f43f5e' }}>
                      Expired? Renew online to <br /> keep using the system.
                    </div>
                    <a
                      href={renewUrl || `${licenseServerUrl}/renew`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest p-2.5 px-4 rounded-lg text-white shadow-md hover:scale-105 transition-transform"
                      style={{ backgroundColor: '#f43f5e' }}
                    >
                      Renew Now
                      <ExternalLink size={12} />
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={handleCheckRenewal}
                    disabled={loading}
                    className="w-full text-center text-[10px] font-black uppercase tracking-widest py-2 rounded-lg transition-all hover:bg-black/5"
                    style={{ color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                  >
                    {loading ? 'Checking...' : 'I have renewed my license'}
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Installation Domain</label>
                  <div className="relative rounded-xl shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Globe className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
                    </div>
                    <input
                      required
                      className="block w-full pl-10 text-[10px] rounded-xl py-2 outline-none opacity-60"
                      style={{ backgroundColor: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                      value={domain}
                      onChange={e => setDomain(normalizeDomain(e.target.value))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Server Hash</label>
                  <div className="relative rounded-xl shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Cpu className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
                    </div>
                    <input
                      readOnly
                      className="block w-full pl-10 text-[10px] rounded-xl py-2 outline-none opacity-60"
                      style={{ backgroundColor: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                      value={serverHash.substring(0, 12) + '...'}
                    />
                  </div>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-4 px-4 rounded-xl shadow-lg text-sm font-black text-white transition-all gap-2 items-center uppercase tracking-widest disabled:opacity-50"
                  style={{ backgroundColor: 'var(--brand)' }}
                >
                  {loading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>Activate System</>
                  )}
                </button>
              </div>

              <p className="text-[10px] text-center font-bold leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                By activating this product, you agree to the terms of service and license agreement of RoyalWeb.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
